import React from "react";
import { useApp } from "../context/AppContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, EmptyState, SectionTitle, fmtHours } from "../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Activity, Timer, RefreshCcw, Layers3, Users } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <GlassCard className="p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}><Icon className="w-5 h-5 text-white" /></div>
      <div><div className="text-2xl font-bold text-slate-900 font-display">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </GlassCard>
  );
}

function Bottlenecks({ perf }) {
  const chart = perf.bottlenecks.map((b) => ({ name: `${b.from.slice(0, 12)}→${b.to.slice(0, 12)}`, hours: b.avg_wait_hours }));
  const max = Math.max(...chart.map((c) => c.hours), 1);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <GlassCard className="p-6">
        <h3 className="font-bold text-slate-900 mb-4">Top bottlenecks (avg wait)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chart} layout="vertical" margin={{ left: 10, right: 20 }}>
            <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(6,182,212,0.05)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} formatter={(v) => [`${v}h`, "Avg wait"]} />
            <Bar dataKey="hours" radius={[0, 8, 8, 0]}>
              {chart.map((c, i) => <Cell key={i} fill={c.hours > max * 0.66 ? "#f59e0b" : "#06b6d4"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </GlassCard>
      <GlassCard className="p-6">
        <h3 className="font-bold text-slate-900 mb-4">Activity durations</h3>
        <div className="space-y-2 max-h-[300px] overflow-y-auto no-scrollbar">
          {perf.activity_performance.map((a, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-slate-700 w-40 truncate">{a.activity}</span>
              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full pm-gradient rounded-full" style={{ width: `${Math.min(100, a.avg_hours / perf.activity_performance[0].avg_hours * 100)}%` }} />
              </div>
              <span className="text-xs font-semibold text-slate-600 w-14 text-right">{fmtHours(a.avg_hours)}</span>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}

function Resources({ datasetId }) {
  const { data, loading } = useFetch(`/datasets/${datasetId}/resources`, { skip: !datasetId });
  if (loading) return <Loading label="Building resource heatmap…" />;
  if (!data) return null;
  const max = Math.max(...data.grid.flatMap((r) => r.values), 1);
  return (
    <GlassCard className="p-6 overflow-x-auto">
      <h3 className="font-bold text-slate-900 mb-4">Resource · Activity heatmap</h3>
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white/80 p-2 text-left text-slate-500 font-semibold">Resource</th>
            {data.activities.map((a) => (
              <th key={a} className="p-2 text-slate-400 font-medium" style={{ writingMode: "vertical-rl", height: 110 }}>{a}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.grid.map((row) => (
            <tr key={row.resource}>
              <td className="sticky left-0 bg-white/80 p-2 font-medium text-slate-700 whitespace-nowrap">{row.resource}</td>
              {row.values.map((v, i) => (
                <td key={i} className="p-0.5">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center text-[10px] font-semibold"
                    style={{ background: v ? `rgba(6,182,212,${0.12 + (v / max) * 0.8})` : "#f1f5f9", color: v / max > 0.5 ? "#fff" : "#475569" }}>
                    {v || ""}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </GlassCard>
  );
}

function Timeline({ datasetId }) {
  const { data, loading } = useFetch(`/datasets/${datasetId}/timeline`, { skip: !datasetId });
  if (loading) return <Loading label="Rendering case timelines…" />;
  if (!data) return null;
  const COLORS = ["#0e7490", "#06b6d4", "#22d3ee", "#0891b2", "#155e75", "#67e8f9", "#0284c7", "#38bdf8"];
  return (
    <GlassCard className="p-6">
      <h3 className="font-bold text-slate-900 mb-4">Case timelines (Gantt)</h3>
      <div className="space-y-3">
        {data.map((c) => (
          <div key={c.case_id} className="flex items-center gap-3" data-testid={`timeline-${c.case_id}`}>
            <span className="text-xs font-mono text-slate-600 w-24 shrink-0">{c.case_id}</span>
            <div className="flex-1 relative h-7 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden">
              {c.activities.map((a, i) => {
                const left = (a.offset_hours / (c.total_hours || 1)) * 100;
                const width = Math.max(1.5, (a.duration_hours / (c.total_hours || 1)) * 100);
                return (
                  <div key={i} title={`${a.activity} · ${fmtHours(a.duration_hours)}`} className="absolute top-1 bottom-1 rounded"
                    style={{ left: `${left}%`, width: `${width}%`, background: COLORS[i % COLORS.length], opacity: 0.85 }} />
                );
              })}
            </div>
            <span className="text-xs text-slate-500 w-14 text-right">{fmtHours(c.total_hours)}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

function Inner() {
  const { datasetId } = useApp();
  const { data: perf, loading } = useFetch(datasetId ? `/datasets/${datasetId}/performance` : null, { skip: !datasetId });
  if (!datasetId) return <EmptyState icon={Activity} title="No dataset selected" />;
  if (loading) return <Loading label="Computing performance metrics…" />;
  if (!perf) return null;

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Performance Analysis" title="Performance & Resources" sub="Bottlenecks, cycle time, rework and resource load" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat icon={Timer} label="Avg cycle time" value={fmtHours(perf.avg_duration_hours)} accent="pm-gradient" />
        <Stat icon={Activity} label="Median cycle" value={fmtHours(perf.median_duration_hours)} accent="bg-gradient-to-br from-indigo-500 to-blue-500" />
        <Stat icon={RefreshCcw} label="Rework cases" value={perf.rework_cases} accent="bg-gradient-to-br from-amber-500 to-orange-500" />
        <Stat icon={Users} label="Resources" value={perf.resources.length} accent="bg-gradient-to-br from-emerald-500 to-teal-500" />
      </div>
      <Tabs defaultValue="bottlenecks">
        <TabsList className="glass rounded-xl p-1">
          <TabsTrigger value="bottlenecks" data-testid="tab-bottlenecks" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><Layers3 className="w-4 h-4 mr-1.5" />Bottlenecks</TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><Users className="w-4 h-4 mr-1.5" />Resources</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="rounded-lg data-[state=active]:pm-gradient data-[state=active]:text-white"><Timer className="w-4 h-4 mr-1.5" />Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="bottlenecks" className="mt-5"><Bottlenecks perf={perf} /></TabsContent>
        <TabsContent value="resources" className="mt-5"><Resources datasetId={datasetId} /></TabsContent>
        <TabsContent value="timeline" className="mt-5"><Timeline datasetId={datasetId} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function Performance() { return <NeedTenant><Inner /></NeedTenant>; }
